<section class="bill-verification-page">
  @if (!showForm()) {
  <div class="summary-shell">
    <div class="d-flex align-items-center gap-3">
      <span class="title-badge">
        <lucide-icon [img]="ShieldCheckIcon" [size]="20"></lucide-icon>
      </span>
      <div>
        <h1 class="page-title-lite">Bill Verification</h1>
      </div>
    </div>
    <div class="summary-grid gap-3">
      @for (stat of heroStats(); track stat.label) {
        <article class="summary-tile">
          <div class="summary-tile-header">
            <div class="summary-tile-label">{{ stat.label }}</div>
            <div class="summary-tile-value">{{ stat.value }}</div>
          </div>
          <div class="summary-tile-track">
            @for (segment of stat.segments; track segment.label) {
              @if (segment.value > 0) {
                <div
                  [class]="statSegmentClass(segment.tone)"
                  [style.width.%]="statSegmentWidth(segment.value, statSegmentTotal(stat))"
                  [attr.title]="segment.label + ': ' + segment.value"
                >
                  {{ segment.value }}
                </div>
              }
            }
          </div>
        </article>
      }
    </div>
  </div>
  <div class="table-card">
    <div class="table-toolbar">
      <div class="table-toolbar-main">
        <div class="card-head">
          <lucide-icon [img]="ReceiptTextIcon" [size]="18"></lucide-icon>
          <span>Seller Bills To Generate</span>
        </div>
        <div class="table-toolbar-buttons">
          <button class="btn btn-outline-secondary d-inline-flex align-items-center gap-2" type="button" (click)="toggleSellerBillFilters()">
            <lucide-icon [img]="FilterIcon" [size]="16"></lucide-icon>
            <span>Filters</span>
          </button>
          <button class="btn btn-primary d-inline-flex align-items-center gap-2" type="button" (click)="openAddInvoice()">
            <lucide-icon [img]="PlusIcon" [size]="16"></lucide-icon>
            <span>Add Invoice</span>
          </button>
        </div>
      </div>
      @if (showSellerBillFilters()) {
      <div class="table-filters">
        <div class="filter-heading">
          <lucide-icon [img]="FilterIcon" [size]="16"></lucide-icon>
          <span>Filters</span>
        </div>
        @if (!isVendor()) {
          <label class="filter-field">
            <span class="filter-label">Client</span>
            <select class="form-select filter-control" [ngModel]="generateClientFilterValue" (ngModelChange)="onGenerateClientFilterChange($event)">
              <option value="">All clients</option>
              @for (vendor of vendors(); track vendor.id) {
                <option [value]="vendor.id">{{ vendor.vendor_name }}</option>
              }
            </select>
          </label>
        }
        <label class="filter-field">
          <span class="filter-label">LOA</span>
          <select class="form-select filter-control" [ngModel]="generateLoaFilterValue" (ngModelChange)="onGenerateLoaFilterChange($event)">
            <option value="">All LOAs</option>
            @for (loa of generateFilterLoas(); track loa.id) {
              <option [value]="loa.id">{{ loa.loa_number }}</option>
            }
          </select>
        </label>
        <label class="filter-field">
          <span class="filter-label">Bill Cycle</span>
          <select class="form-select filter-control" [ngModel]="generateBillCycleFilterValue" (ngModelChange)="onGenerateBillCycleFilterChange($event)">
            <option value="">All cycles</option>
            @for (cycle of sellerBillCycleOptions(); track cycle) {
              <option [value]="cycle">{{ cycle }}</option>
            }
          </select>
        </label>
        <label class="filter-field">
          <span class="filter-label">Bill Date From</span>
          <input class="form-control filter-control" type="date" [ngModel]="generateBillDateFromValue" (ngModelChange)="onGenerateBillDateFromChange($event)" />
        </label>
        <label class="filter-field">
          <span class="filter-label">Bill Date To</span>
          <input class="form-control filter-control" type="date" [ngModel]="generateBillDateToValue" (ngModelChange)="onGenerateBillDateToChange($event)" />
        </label>
        <label class="filter-field filter-field-search">
          <span class="filter-label">Search</span>
          <input
            class="form-control filter-control"
            [ngModel]="generateSearchValue"
            (ngModelChange)="onGenerateSearchChange($event)"
            placeholder="Seller, LOA, cycle, status"
          />
        </label>
        <div class="filter-actions">
          <button class="btn btn-primary" type="button" (click)="applySellerBillFilters()">Go</button>
          <button class="btn btn-outline-secondary" type="button" (click)="clearSellerBillFilters()">Clear</button>
        </div>
      </div>
      }
    </div>

    @if (loading()) {
      <div class="empty-state">Loading seller bill rows...</div>
    } @else if (!filteredSellerBillRows().length) {
      <div class="empty-state">No seller bill rows found from LOA bill configuration.</div>
    } @else {
      <div class="table-responsive">
        <table class="table align-middle mb-0">
          <thead>
            <tr>
              <th>S.No</th>
              <th class="d-none">Application</th>
              <th>Seller</th>
              <th>LOA</th>
              <th class="d-none">Bill Type</th>
              <th>Bill Cycle</th>
              <th>Bill Date</th>
              <th>Period</th>
              <th>Approved Quantum</th>
              <th>Billed Quantum</th>
              <th>LOA Rate</th>
              <th>Rate</th>
              <th>Expected Amount</th>
              <th>Submitted Amount</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            @for (row of paginatedSellerBillRows(); track row.rowKey; let rowIndex = $index) {
                <tr>
                  <td>{{ sellerBillSerialNumber(rowIndex) }}</td>
                  <td class="d-none" [title]="row.applicationNumberTooltip || row.applicationNumber">{{ row.applicationNumber }}</td>
                  <td>{{ row.sellerName }}</td>
                  <td>{{ row.loaNumber }}</td>
                  <td class="d-none">{{ row.billType }}</td>
                  <td>{{ row.billCycle }}</td>
                  <td>{{ formatDate(row.billDate) }}</td>
                  <td>{{ formatDate(row.billPeriodFrom) }} to {{ formatDate(row.billPeriodTo) }}</td>
                  <td>{{ formatQuantum(row.totalQuantum) }}</td>
                  <td>{{ formatQuantum(row.billedQuantum) }}</td>
                  <td>{{ row.configuredRateLabel }}</td>
                  <td>{{ row.submittedRate ?? '-' }}</td>
                  <td>{{ formatAmount(row.expectedAmount) }}</td>
                  <td>{{ formatAmount(row.submittedAmount) }}</td>
                  <td>
                    <div class="status-stack" [attr.title]="row.statusTooltip">
                      @if (shouldShowVerificationStatus(row)) {
                        <span
                          class="status-badge"
                          [ngClass]="'status-' + statusVariant(row.verificationStatus)"
                        >{{ displayVerificationStatus(row.verificationStatus) }}</span>
                      }
                      @if (row.verificationId) {
                        <span
                          class="status-badge"
                          [ngClass]="'status-' + approvalStatusVariant(row.approvalStatus)"
                        >{{ displayApprovalStatus(row) }}</span>
                      }
                    </div>
                  </td>
                  <td>
                    <div class="row-action-group">
                      @if (shouldShowOpenVerification(row)) {
                        <button class="btn btn-sm btn-outline-primary action-pill-btn" type="button" (click)="useSellerBill(row)">
                          {{ row.verificationId ? 'Edit Invoice' : 'Add Invoice' }}
                        </button>
                      }
                      @if (shouldShowReviewDecision(row)) {
                        <button
                          class="btn btn-sm btn-dark action-pill-btn"
                          type="button"
                          [disabled]="approvalActionLoadingId() === row.verificationId"
                          (click)="openApprovalDecisionDialog(row)"
                        >
                          Review
                        </button>
                      }
                    </div>
                  </td>
                </tr>
            }
          </tbody>
        </table>
      </div>
      <div class="pager">
        <button class="pager-btn" type="button" [disabled]="sellerBillsCurrentPage() <= 1" (click)="goToSellerBillsPage(sellerBillsCurrentPage() - 1)">«</button>
        <span class="page-no">{{ sellerBillsCurrentPage() }}</span>
        <button class="pager-btn" type="button" [disabled]="sellerBillsCurrentPage() >= sellerBillTotalPages()" (click)="goToSellerBillsPage(sellerBillsCurrentPage() + 1)">»</button>
        <select class="form-select form-select-sm page-size" [ngModel]="sellerBillsPageSize()" (ngModelChange)="onSellerBillsPageSizeChange($event)">
          @for (size of sellerBillsPageSizeOptions; track size) {
            <option [ngValue]="size">{{ size }}/page</option>
          }
        </select>
      </div>
    }
  </div>
  }
@if (showForm()) {
  <div class="content-grid" #invoiceFormSection>
    <div class="form-card">
      <div class="form-card-head">
        <div class="card-head">
          <lucide-icon [img]="UploadIcon" [size]="18"></lucide-icon>
          <span>{{ editingId() ? 'Edit Invoice' : 'Invoice Actions' }}</span>
        </div>
        <button class="btn btn-outline-secondary icon-btn" type="button" (click)="closeForm()" aria-label="Close invoice form">
          <lucide-icon [img]="CloseIcon" [size]="16"></lucide-icon>
        </button>
      </div>

        @if (editingId()) {
          <div class="row g-3 form-grid">
            <div class="col-md-6">
              <label class="form-label">Invoice File</label>
              <div class="file-input-wrap">
                <input
                  class="form-control"
                  type="file"
                  (change)="onFileSelected($event)"
                  [disabled]="extractingInvoice()"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                />
                @if (extractingInvoice()) {
                  <span class="file-input-loader" aria-live="polite">
                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    <span>Reading...</span>
                  </span>
                }
              </div>
            </div>

            <div class="col-md-6">
              <label class="form-label">Seller</label>
              <select class="form-select" [(ngModel)]="sellerVendorId" (ngModelChange)="onSellerChange()" [disabled]="isVendor()">
                @if (!isVendor()) {
                  <option value="">Select seller</option>
                }
                @for (vendor of vendors(); track vendor.id) {
                  <option [value]="vendor.id">{{ vendor.vendor_name }}</option>
                }
              </select>
            </div>

            <div class="col-md-6">
              <label class="form-label">Buyer</label>
              <select class="form-select" [(ngModel)]="buyerVendorId" (ngModelChange)="onBuyerChange()">
                <option value="">Select buyer</option>
                @for (vendor of availableBuyers(); track vendor.id) {
                  <option [value]="vendor.id">{{ vendor.vendor_name }}</option>
                }
              </select>
            </div>

            <div class="col-md-6">
              <label class="form-label">Contract</label>
              <select class="form-select" [(ngModel)]="contractId" (ngModelChange)="onContractChange($event)">
                <option value="">Select contract</option>
                @for (contract of availableContracts(); track contract.id) {
                  <option [value]="contract.id">{{ contract.contract_number }}</option>
                }
              </select>
            </div>

            <div class="col-md-6">
              <label class="form-label">LOA</label>
              <select class="form-select" [(ngModel)]="loaId" (ngModelChange)="onLoaChange($event)">
                <option value="">Select LOA</option>
                @for (loa of availableLoas(); track loa.id) {
                  <option [value]="loa.id">{{ loa.loa_number }}</option>
                }
              </select>
            </div>

            <div class="col-md-4">
              <label class="form-label">Invoice Number</label>
              <input class="form-control" [(ngModel)]="invoiceNumber" placeholder="Enter invoice number" />
            </div>

            <div class="col-md-4">
              <label class="form-label">Invoice Date</label>
              <input class="form-control" type="date" [(ngModel)]="invoiceDate" />
            </div>

            <div class="col-md-4">
              <label class="form-label">Bill Date</label>
              <input class="form-control" type="date" [(ngModel)]="billDate" />
            </div>

            <div class="col-12 d-flex justify-content-end">
              <button class="btn btn-outline-secondary" type="button" [disabled]="fetchingApproval()" (click)="fetchApprovalValues()">
                {{ fetchingApproval() ? 'Fetching approval...' : 'Fetch Quantum & Amount from Approval' }}
              </button>
            </div>

            <div class="col-md-4">
              <label class="form-label">Bill Period From</label>
              <input class="form-control" type="date" [(ngModel)]="billPeriodFrom" />
            </div>

            <div class="col-md-4">
              <label class="form-label">Bill Period To</label>
              <input class="form-control" type="date" [(ngModel)]="billPeriodTo" />
            </div>

            <div class="col-md-4">
              <label class="form-label">Billed Quantum</label>
              <input class="form-control" type="number" step="0.0001" [ngModel]="billedQuantum" (ngModelChange)="updateEditBilledQuantum($event)" placeholder="Invoice quantum in MWh" />
            </div>

            <div class="col-md-6">
              <label class="form-label">Submitted Rate</label>
              <input class="form-control" type="number" step="0.0001" [ngModel]="submittedRate" (ngModelChange)="updateEditSubmittedRate($event)" placeholder="Configured bill rate" />
            </div>

            <div class="col-md-6">
              <label class="form-label">Submitted Amount</label>
              <input class="form-control" type="number" step="0.01" [ngModel]="submittedAmount" placeholder="Invoice total amount" readonly />
            </div>
            <div class="col-12">
              <label class="form-label">Remarks</label>
              <textarea class="form-control" rows="3" [(ngModel)]="remarks" placeholder="Optional remarks"></textarea>
            </div>
          </div>
        } @else {
          <div class="row g-3 form-grid">
            <div class="col-md-6">
              <label class="form-label">Invoice File</label>
              <div class="file-input-wrap">
                <input
                  class="form-control"
                  type="file"
                  (change)="onFileSelected($event)"
                  [disabled]="extractingInvoice()"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                />
                @if (extractingInvoice()) {
                  <span class="file-input-loader" aria-live="polite">
                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    <span>Reading...</span>
                  </span>
                }
              </div>
            </div>

            <div class="col-md-6">
              <label class="form-label">Seller</label>
              <select class="form-select" [(ngModel)]="sellerVendorId" (ngModelChange)="onSellerChange()" [disabled]="isVendor()">
                @if (!isVendor()) {
                  <option value="">Select seller</option>
                }
                @for (vendor of vendors(); track vendor.id) {
                  <option [value]="vendor.id">{{ vendor.vendor_name }}</option>
                }
              </select>
            </div>

            <div class="col-md-6">
              <label class="form-label">Buyer</label>
              <select class="form-select" [(ngModel)]="buyerVendorId" (ngModelChange)="onBuyerChange()">
                <option value="">Select buyer</option>
                @for (vendor of availableBuyers(); track vendor.id) {
                  <option [value]="vendor.id">{{ vendor.vendor_name }}</option>
                }
              </select>
            </div>

            <div class="col-md-6">
              <label class="form-label">Contract</label>
              <select class="form-select" [(ngModel)]="contractId" (ngModelChange)="onContractChange($event)">
                <option value="">Select contract</option>
                @for (contract of availableContracts(); track contract.id) {
                  <option [value]="contract.id">{{ contract.contract_number }}</option>
                }
              </select>
            </div>

            <div class="col-md-6">
              <label class="form-label">LOA</label>
              <select class="form-select" [(ngModel)]="loaId" (ngModelChange)="onLoaChange($event)">
                <option value="">Select LOA</option>
                @for (loa of availableLoas(); track loa.id) {
                  <option [value]="loa.id">{{ loa.loa_number }}</option>
                }
              </select>
            </div>

            <div class="col-md-4">
              <label class="form-label">Invoice Number</label>
              <input class="form-control" [(ngModel)]="invoiceNumber" placeholder="Enter invoice number" />
            </div>

            <div class="col-md-4">
              <label class="form-label">Invoice Date</label>
              <input class="form-control" type="date" [(ngModel)]="invoiceDate" />
            </div>

            <div class="col-12">
              <label class="form-label">Remarks</label>
              <textarea class="form-control" rows="3" [(ngModel)]="remarks" placeholder="Optional remarks"></textarea>
            </div>
          </div>

          <div class="bill-lines">
            <div class="bill-lines-head">
              <div>
                <div class="bill-lines-title">Bill Details</div>
                <div class="bill-lines-copy">Enter the bill period and invoice values for the selected seller invoice.</div>
              </div>
              <button class="btn btn-outline-primary btn-sm" type="button" (click)="addBillEntry()">Add Bill Line</button>
            </div>

            @for (entry of billEntries(); track $index) {
              <div class="bill-line-card">
                <div class="bill-line-head">
                  <strong>Seller Invoice {{ $index + 1 }}</strong>
                  @if (billEntries().length > 1) {
                    <button class="btn btn-sm btn-outline-danger" type="button" (click)="removeBillEntry($index)">Remove</button>
                  }
                </div>
                <div class="row g-3">
                  <div class="col-md-4">
                    <label class="form-label">Bill Date</label>
                    <input class="form-control" type="date" [ngModel]="entry.billDate" (ngModelChange)="updateBillEntry($index, { billDate: $event })" />
                  </div>
                  @if ($index === 0) {
                    <div class="col-12 d-flex justify-content-end">
                      <button class="btn btn-outline-secondary" type="button" [disabled]="fetchingApproval()" (click)="fetchApprovalValues()">
                      {{ fetchingApproval() ? 'Fetching approval...' : 'Fetch Quantum & Amount from Approval' }}
                      </button>
                    </div>
                  }
                  <div class="col-md-4">
                    <label class="form-label">Bill Period From</label>
                    <input class="form-control" type="date" [ngModel]="entry.billPeriodFrom" (ngModelChange)="updateBillEntry($index, { billPeriodFrom: $event })" />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Bill Period To</label>
                    <input class="form-control" type="date" [ngModel]="entry.billPeriodTo" (ngModelChange)="updateBillEntry($index, { billPeriodTo: $event })" />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Billed Quantum</label>
                    <input class="form-control" type="number" step="0.0001" [ngModel]="entry.billedQuantum" (ngModelChange)="updateBillEntry($index, { billedQuantum: $event })" placeholder="Invoice quantum in MWh" />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Submitted Rate</label>
                    <input class="form-control" type="number" step="0.0001" [ngModel]="entry.submittedRate" (ngModelChange)="updateBillEntry($index, { submittedRate: $event })" placeholder="Configured bill rate" />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Submitted Amount</label>
                    <input class="form-control" type="number" step="0.01" [ngModel]="entry.submittedAmount" placeholder="Invoice total amount" readonly />
                  </div>
                </div>
              </div>
            }
          </div>
        }

        <div class="mt-3 d-flex justify-content-end gap-2">
          <button class="btn btn-outline-secondary px-4" type="button" (click)="cancelEdit()">Cancel</button>
          <button class="btn btn-primary px-4" type="button" [disabled]="saving()" (click)="submit()">
            {{ saving() ? 'Saving...' : (editingId() ? 'Update Verification' : 'Verify Bill') }}
          </button>
        </div>
    </div>

    <div class="summary-card">
      <div class="card-head">
        <lucide-icon [img]="ReceiptTextIcon" [size]="18"></lucide-icon>
        <span>Verification Rule</span>
      </div>
      <ul class="summary-list">
        <li>Matches seller and LOA ownership.</li>
        <li>Picks the LOA bill configuration effective on the selected bill date.</li>
        <li>Checks submitted rate against the configured LOA billing rate.</li>
        <li>Flags bill dates outside the LOA effective period.</li>
      </ul>
      <div class="note-box">Expected amount is calculated from punched seller LOA application approval MWh for the same billing period.</div>
    </div>
    
  </div>
 }
  <app-dialog
    [open]="approvalReasonDialogOpen()"
    [title]="'Bill Approval Decision'"
    (closed)="closeApprovalReasonDialog()"
  >
    <div class="approval-dialog-body">
      <div class="approval-dialog-hero">
        <div>
          <div class="approval-dialog-eyebrow">Invoice Details</div>
          <div class="approval-dialog-title">Invoice {{ pendingApprovalInvoiceNumber }}</div>
          @if (pendingApprovalSummary) {
            <div class="approval-dialog-copy">
              LOA: {{ pendingApprovalSummary.loaNumber }} | Bill Period:
              {{ formatDate(pendingApprovalSummary.billPeriodFrom) }} to {{ formatDate(pendingApprovalSummary.billPeriodTo) }} |
              Quantum: {{ formatQuantum(pendingApprovalSummary.quantum) }} | Rate: {{ pendingApprovalSummary.rate ?? '-' }} |
              Amount: {{ formatAmount(pendingApprovalSummary.amount) }}
            </div>
          }
        </div>
        <div class="status-badge status-secondary">{{ approvalActionLabel(pendingApprovalAction) }}</div>
      </div>
      <div class="approval-option-grid">
        <button
          class="approval-option-card"
          type="button"
          [class.active]="pendingApprovalAction === 'approve'"
          (click)="pendingApprovalAction = 'approve'"
        >
          <span class="approval-option-title">Approve</span>
          <span class="approval-option-copy">{{ approvalActionDescription('approve') }}</span>
        </button>
        <button
          class="approval-option-card reject"
          type="button"
          [class.active]="pendingApprovalAction === 'reject'"
          (click)="pendingApprovalAction = 'reject'"
        >
          <span class="approval-option-title">Reject</span>
          <span class="approval-option-copy">{{ approvalActionDescription('reject') }}</span>
        </button>
        <button
          class="approval-option-card force"
          type="button"
          [class.active]="pendingApprovalAction === 'force-approve'"
          (click)="pendingApprovalAction = 'force-approve'"
        >
          <span class="approval-option-title">Force Approve</span>
          <span class="approval-option-copy">{{ approvalActionDescription('force-approve') }}</span>
        </button>
      </div>
      <label class="form-label" for="bill-approval-reason">
        Remark
        <span class="approval-required">*</span>
      </label>
      <textarea
        id="bill-approval-reason"
        class="form-control"
        rows="4"
        [(ngModel)]="approvalReason"
        placeholder="Enter remark"
      ></textarea>
      <p class="approval-dialog-copy">
        A remark is required for {{ approvalActionLabel(pendingApprovalAction).toLowerCase() }}.
      </p>
      <div class="approval-dialog-actions">
        <button class="btn btn-outline-secondary" type="button" (click)="closeApprovalReasonDialog()">Cancel</button>
        <button class="btn btn-primary" type="button" (click)="submitApprovalReason()">
          Submit {{ approvalActionLabel(pendingApprovalAction) }}
        </button>
      </div>
    </div>
  </app-dialog>
  <div class="table-card d-none">
    <div class="table-toolbar">
      <div class="table-toolbar-main">
        <div class="card-head">
          <lucide-icon [img]="SearchIcon" [size]="18"></lucide-icon>
          <span>Verification History</span>
        </div>
        <div class="table-toolbar-copy">Review all submitted bills, including pending verification records.</div>
      </div>
      <div class="table-filters">
        @if (!isVendor()) {
          <label class="filter-field">
            <span class="filter-label">Client</span>
            <select class="form-select filter-control" [ngModel]="clientFilter()" (ngModelChange)="clientFilter.set($event); onClientFilterChange()">
              <option value="">All clients</option>
              @for (vendor of vendors(); track vendor.id) {
                <option [value]="vendor.id">{{ vendor.vendor_name }}</option>
              }
            </select>
          </label>
        }
        <label class="filter-field">
          <span class="filter-label">LOA</span>
          <select class="form-select filter-control" [ngModel]="loaFilter()" (ngModelChange)="onLoaFilterChange($event)">
            <option value="">All LOAs</option>
            @for (loa of filterLoas(); track loa.id) {
              <option [value]="loa.id">{{ loa.loa_number }}</option>
            }
          </select>
        </label>
        <label class="filter-field filter-field-search">
          <span class="filter-label">Search</span>
          <input class="form-control filter-control" [ngModel]="search()" (ngModelChange)="search.set($event)" placeholder="Invoice, seller, status" />
        </label>
      </div>
    </div>

    @if (loading()) {
      <div class="empty-state">Loading bill verification records...</div>
    } @else if (!visibleRows().length) {
      <div class="empty-state">No bill verification records found.</div>
    } @else {
      <div class="table-responsive">
        <table class="table align-middle mb-0">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Invoice</th>
              <th>Seller</th>
              <th>LOA</th>
              <th>Bill Date</th>
              <th>Period</th>
              <th>Submitted Rate</th>
              <th>Expected Rate</th>
              <th>Submitted Amount</th>
              <th>Expected Amount</th>
              <th>Status</th>
              <th>File</th>
              <!-- <th>Verification Notes</th> -->
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            @for (row of visibleRows(); track row.id; let rowIndex = $index) {
              <tr>
                <td>{{ rowIndex + 1 }}</td>
                <td>
                  <div class="fw-semibold">{{ row.invoice_number }}</div>
                  <div class="small text-muted">{{ row.bill_type }} bill</div>
                  <!-- @if (row.invoice_date) {
                    <div class="small text-muted">{{ formatDate(row.invoice_date) }}</div>
                  } -->
                </td>
                <td>{{ row.seller_name }}</td>
                <td>{{ row.loa_number }}</td>
                <td>{{ formatDate(row.bill_date) }}</td>
                <td>{{ formatDate(row.bill_period_from) }} to {{ formatDate(row.bill_period_to) }}</td>
                <td>{{ row.submitted_rate ?? '-' }}</td>
                <td>{{ row.expected_rate ?? '-' }}</td>
                <td>{{ formatAmount(row.submitted_amount) }}</td>
                <td>{{ formatAmount(row.expected_amount) }}</td>
                <td>
                  <div class="status-stack" [attr.title]="statusTooltip(row)">
                    <span
                      class="status-badge"
                      [ngClass]="'status-' + statusVariant(row.verification_status)"
                    >{{ displayVerificationStatus(row.verification_status) }}</span>
                    <span
                      class="status-badge"
                      [ngClass]="'status-' + approvalStatusVariant(row.approval_status)"
                    >{{ displayApprovalStatus(row) }}</span>
                  </div>
                </td>
                <td>
                  @if (row.invoice_file_original_name && row.invoice_download_url) {
                    <button
                      class="btn btn-sm btn-outline-primary icon-btn"
                      type="button"
                      (click)="downloadFile(row)"
                      [title]="row.invoice_file_original_name"
                      aria-label="Download invoice file"
                    >
                      <lucide-icon [img]="DownloadIcon" [size]="16"></lucide-icon>
                    </button>
                  } @else {
                    <span class="text-muted">No file</span>
                  }
                </td>
                <!-- <td>
                  @for (note of row.verification_notes; track note) {
                    <div class="note-line">{{ note }}</div>
                  }
                </td> -->
                <td>
                  <div class="d-flex gap-2">
                    <button class="btn btn-sm btn-outline-dark" type="button" (click)="edit(row)">Edit</button>
                    <button class="btn btn-sm btn-outline-danger icon-btn" type="button" (click)="delete(row)" aria-label="Delete invoice">
                      <lucide-icon [img]="TrashIcon" [size]="16"></lucide-icon>
                    </button>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </div>
</section>
